import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { getServiceByCategory } from '~/services/serviceService';
import Title from '~/components/Title';
import styles from './ServiceCategory.module.scss';
import Card from '~/components/CardContent/CardContent';
import { getCategoriesByType } from '~/services/categoryService';
import routes from '~/config/routes';
import { Helmet } from 'react-helmet';
import { Empty } from 'antd';

const cx = classNames.bind(styles);

function ServiceCategory() {
    const location = useLocation();
    const [service, setService] = useState([]);
    const [categoryId, setCategoryId] = useState(null);
    const [subcategoryId, setSubcategoryId] = useState(null);
    const [categoryName, setCategoryName] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const servicePerPage = 6;

    const extractSlugFromPathname = (pathname) => {
        const parts = pathname.split('/');
        return parts.length > 2 ? parts[2] : null;
    };

    const slug = extractSlugFromPathname(location.pathname);

    useEffect(() => {
        async function fetchCategory() {
            try {
                const categories = await getCategoriesByType(1);
                let category = categories.find((cat) => cat.slug === slug);

                if (!category) {
                    for (const cat of categories) {
                        const subcategory = cat.subcategories.find((subcat) => subcat.slug === slug);
                        if (subcategory) {
                            setCategoryId(cat._id);
                            setSubcategoryId(subcategory._id);
                            setCategoryName(subcategory.name);
                            return;
                        }
                    }
                } else {
                    setCategoryId(category._id);
                    setSubcategoryId(null);
                    setCategoryName(category.name);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        }

        if (slug) {
            fetchCategory();
        }
    }, [slug]);

    useEffect(() => {
        async function fetchServiceCategory() {
            try {
                let data = [];
                if (subcategoryId) {
                    data = await getServiceByCategory(subcategoryId);
                } else if (categoryId) {
                    data = await getServiceByCategory(categoryId);

                    if (!data.service) {
                        const categories = await getCategoriesByType(3);
                        const parentCategory = categories.find((cat) => cat._id === categoryId);

                        if (parentCategory && parentCategory.subcategories) {
                            const subcategoryServices = await Promise.all(
                                parentCategory.subcategories.map(async (subcat) => {
                                    const services = await getServiceByCategory(subcat._id);
                                    if (services.service) {
                                        return services.filter((service) => service._id || service.name);
                                    } else {
                                        return null;
                                    }
                                }),
                            );

                            data = subcategoryServices.filter(Boolean).flat();
                        }
                    }
                }
                setService(data.service);
            } catch (error) {
                console.error('Error fetching service:', error);
            }
        }

        if (categoryId || subcategoryId) {
            fetchServiceCategory();
        }
    }, [categoryId, subcategoryId, slug]);

    const indexOfLastService = currentPage * servicePerPage;
    const indexOfFirstService = indexOfLastService - servicePerPage;
    const currentServiceCategory = service.slice(indexOfFirstService, indexOfLastService);

    const totalPages = Math.ceil(service.length / servicePerPage);

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const renderServiceCategory = () => {
        if (currentServiceCategory.length === 0) {
            return (
                <>
                    <div />
                    <Empty className={cx('empty-element')} description="Đang cập nhật..." />
                    <div />
                </>
            );
        }

        return currentServiceCategory.map((serviceItem, index) => (
            <Link to={`${routes.services}/${slug}/${serviceItem._id}`} key={serviceItem._id}>
                <Card
                    key={index}
                    title={serviceItem.name}
                    image={`${process.env.REACT_APP_HOST}/${serviceItem.image}`}
                    summary={serviceItem.summary}
                    createdAt={serviceItem.createdAt}
                    views={serviceItem.views}
                />
            </Link>
        ));
    };

    const renderPagination = () => {
        return (
            <div className={cx('pagination')}>
                <div className={cx('pageButton')} onClick={() => handlePageChange(currentPage - 1)}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                </div>
                {Array.from({ length: totalPages }, (_, index) => (
                    <div
                        key={index}
                        className={cx('pageButton', { active: currentPage === index + 1 })}
                        onClick={() => handlePageChange(index + 1)}
                    >
                        {index + 1}
                    </div>
                ))}
                <div className={cx('pageButton')} onClick={() => handlePageChange(currentPage + 1)}>
                    <FontAwesomeIcon icon={faArrowRight} />
                </div>
            </div>
        );
    };

    return (
        <div className={cx('container')}>
            <Helmet>
                <title>{categoryName} | TDC DAKLAK</title>
                <meta name="description" content={`Xem các dịch vụ liên quan đến ${categoryName} trên TDC DAKLAK.`} />
                <meta name="keywords" content={`${categoryName}, dịch vụ, TDC DAKLAK`} />
            </Helmet>
            <Title text={categoryName} />
            <div className={cx('serviceGrid')}>{renderServiceCategory()}</div>
            {renderPagination()}
        </div>
    );
}

export default ServiceCategory;
